import { BackrsFormField } from '@backrs_ui/common-ui';

import { AutocompleteResult } from '@backrs_ui/grow-ui/src/redux/onboarding/onboarding.api';

import { OnboardingQuestion } from '../onboarding.types';
import { useGetCareerPathOptions } from '../questions/questions.shared';

type UseOnboardingFormFieldsParams = Readonly<{
  currentQuestion?: OnboardingQuestion;
  questionKeys: string[];
  currentQuestionIndex: number;
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: React.Dispatch<React.SetStateAction<AutocompleteResult[]>>;
  handleInputChange: (input: string, endpoint: string) => void;
}>;

export const useOnboardingFormFields = ({
  currentQuestion,
  questionKeys,
  currentQuestionIndex,
  autoCompleteOptions,
  setAutoCompleteOptions,
  handleInputChange,
}: UseOnboardingFormFieldsParams): BackrsFormField[] => {
  // Retrieve special career path fields
  const careerPathFields = useGetCareerPathOptions();

  if (!currentQuestion) {
    return [];
  }

  if (currentQuestion.question.type === 'FORM') {
    return handleFormType({
      currentQuestion,
      careerPathFields,
      autoCompleteOptions,
      setAutoCompleteOptions,
      handleInputChange,
    });
  }

  return handleRadioCheckFields({
    currentQuestion,
    questionKeys,
    currentQuestionIndex,
  });
};

type HandleRadioCheckFieldsParams = Readonly<{
  currentQuestion: OnboardingQuestion;
  questionKeys: string[];
  currentQuestionIndex: number;
}>;

const handleRadioCheckFields = ({
  currentQuestion,
  questionKeys,
  currentQuestionIndex,
}: HandleRadioCheckFieldsParams) => {
  if (currentQuestion.question.type !== 'RADIO' && currentQuestion.question.type !== 'CHECKBOX') {
    return [];
  }

  const radioCheckFields: BackrsFormField[] = [
    {
      name: questionKeys[currentQuestionIndex],
      label: currentQuestion.title,
      type: 'text',
      rules: currentQuestion.question.rules,
      selectOptions: {
        type: currentQuestion.question.type === 'RADIO' ? 'SINGLE' : 'MULTIPLE',
        options: currentQuestion.question.options,
      },
    },
  ];

  return radioCheckFields;
};

type HandleFormTypeParams = Readonly<{
  currentQuestion: OnboardingQuestion;
  careerPathFields: BackrsFormField[];
  autoCompleteOptions: AutocompleteResult[];
  setAutoCompleteOptions: React.Dispatch<React.SetStateAction<AutocompleteResult[]>>;
  handleInputChange: (input: string, endpoint: string) => void;
}>;

const handleFormType = ({
  currentQuestion,
  careerPathFields,
  autoCompleteOptions,
  setAutoCompleteOptions,
  handleInputChange,
}: HandleFormTypeParams): BackrsFormField[] => {
  if (currentQuestion.question.type !== 'FORM') {
    return [];
  }

  return currentQuestion.question.fields.flatMap((field) => {
    // Replace the special field with career path options if needed
    if (field.name === 'CAREER_PATH_DROPDOWNS') {
      return careerPathFields;
    }

    // For fields that have an autocomplete endpoint, handle the transformation
    if (field.autoCompleteEndpoint) {
      const autoCompleteEndpoint = field.autoCompleteEndpoint;
      const existingOption = autoCompleteOptions.find(
        (option) => option.endpoint === autoCompleteEndpoint,
      );

      const updatedField: BackrsFormField = {
        ...field,
        selectOptions: {
          ...field.selectOptions,
          type: 'MULTIPLE',
          // Use values from state if available, otherwise default to an empty array
          options:
            existingOption && existingOption.values.length > 0
              ? existingOption.values.map((value) => ({
                  label: value.label,
                  value: value.value,
                }))
              : [],
        },
        onInputChange: (input: string) => handleInputChange(input, autoCompleteEndpoint),
        onOptionSelected: () =>
          setAutoCompleteOptions((prev) =>
            prev.map((opt) =>
              opt.endpoint === autoCompleteEndpoint ? { ...opt, searchTerm: '', values: [] } : opt,
            ),
          ),
      };

      // If the endpoint is not yet in state, add it
      if (!existingOption) {
        setAutoCompleteOptions((prev) => [
          ...prev,
          { endpoint: autoCompleteEndpoint, searchTerm: '', values: [] },
        ]);
      }

      return updatedField;
    }

    if (field.type === 'tel') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\d{10}$/,
            message: 'Phone number must be 10 digits',
          },
          required: 'Phone number is required',
        },
      };

      return updatedField;
    }

    if (field.name === 'zip_code') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\d{5}$/,
            message: 'Zip code must be 5 digits',
          },
          required: 'Zip code is required',
        },
      };

      return updatedField;
    }

    if (field.type === 'email') {
      const updatedField: BackrsFormField = {
        ...field,
        rules: {
          pattern: {
            value: /^\S+@\S+\.\S+$/,
            message: 'Invalid email address',
          },
          required: 'Email is required',
        },
      };

      return updatedField;
    }

    return field;
  });
};
